/**
 * @fileoverview Date Navigator — prev/next + range label + event count
 * Adapted from upstream: reads `view` from context instead of prop.
 *
 * Exposes two render slots so the header can place the title row and
 * the navigation controls on separate visual rows.
 */

import { useMemo } from "react";
import { formatDate } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";

import { getEventsCount, navigateDate, rangeText } from "@/calendar/helpers";

import type { IEvent } from "@/calendar/interfaces";

interface IProps {
  events: IEvent[];
}

export function DateNavigatorTitle({ events }: IProps) {
  const { view, selectedDate } = useCalendar();

  const month = formatDate(selectedDate, "MMMM");
  const year = selectedDate.getFullYear();

  const eventCount = useMemo(
    () => getEventsCount(events, selectedDate, view),
    [events, selectedDate, view],
  );

  return (
    <div className="flex items-center gap-2">
      <span className="whitespace-nowrap text-lg font-semibold">
        {month} {year}
      </span>
      <Badge variant="outline" className="px-1.5">
        {eventCount} {eventCount === 1 ? "event" : "events"}
      </Badge>
    </div>
  );
}

export function DateNavigatorNav() {
  const { view, selectedDate, setSelectedDate } = useCalendar();

  const handlePrevious = () =>
    setSelectedDate(navigateDate(selectedDate, view, "previous"));
  const handleNext = () =>
    setSelectedDate(navigateDate(selectedDate, view, "next"));

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-7 [&_svg]:size-4"
        onClick={handlePrevious}
      >
        <ChevronLeft />
      </Button>

      <p className="whitespace-nowrap text-sm text-muted-foreground">
        {rangeText(view, selectedDate)}
      </p>

      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-7 [&_svg]:size-4"
        onClick={handleNext}
      >
        <ChevronRight />
      </Button>
    </>
  );
}
